// Estrutura compartilhada por todas as telas — extraída literalmente de
// entrevista-sergio-gargantini.html (raiz do projeto "Lorena Magazine").
// Ver DOCS/00-visao-geral.md para o porquê de cada peça.
//
// IMPORTANTE: isto é a fonte única de verdade. Não duplique este CSS/HTML em
// nenhum outro lugar (preview, export, captura) — todos importam daqui.

export const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Anton&family=Big+Shoulders:wght@200;300;400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,300;8..60,400&display=swap"

export const BASE_CSS = `
  :root{
    --ink:#1d1d1b;
    --ghost:#e8e8e8;
    --paper:#ffffff;
  }
  *{margin:0;padding:0;box-sizing:border-box;}
  html{scroll-behavior:smooth;}
  body{
    background:#c9c6c0;
    font-family:'Big Shoulders',sans-serif;
    color:var(--ink);
    -webkit-font-smoothing:antialiased;
  }
  .deck{
    max-width:640px;
    margin:0 auto;
    background:var(--paper);
    box-shadow:0 0 60px rgba(0,0,0,.25);
  }
  .slide{
    position:relative;
    width:100%;
    aspect-ratio:1080/1350;
    background:var(--paper);
    display:flex;
    flex-direction:column;
  }
  .slide + .slide{
    border-top:1px solid #eee;
  }
  .logo{
    height:22px;
    z-index:5;
    flex:0 0 auto;
  }
  .logo img{height:100%; display:block;}

  .reveal{
    opacity:0;
    transform:translateY(36px);
    transition:opacity .9s cubic-bezier(.19,1,.22,1), transform .9s cubic-bezier(.19,1,.22,1);
    will-change:opacity, transform;
  }
  .reveal.is-in{opacity:1; transform:translateY(0);}
  .r1{transition-delay:.05s;}
  .r2{transition-delay:.18s;}
  .r3{transition-delay:.32s;}
  .r4{transition-delay:.46s;}
  .r5{transition-delay:.6s;}

  .reveal-wipe{
    position:relative;
    overflow:hidden;
  }
  .reveal-wipe::after{
    content:"";
    position:absolute; inset:0;
    background:var(--paper);
    transform-origin:left;
    transform:scaleX(1);
    transition:transform .8s cubic-bezier(.65,0,.15,1);
    z-index:2;
  }
  .reveal-wipe.is-in::after{transform:scaleX(0);}

  .img-reveal img,
  .img-reveal .ph{
    transform:scale(1.12);
    transition:transform 1.4s cubic-bezier(.19,1,.22,1);
  }
  .img-reveal.is-in img,
  .img-reveal.is-in .ph{transform:scale(1);}
`

/** Script de rail/contador/reveal — idêntico ao de entrevista-sergio-gargantini.html, agnóstico à quantidade de `.slide`. */
export const REVEAL_SCRIPT = `
(function(){
  var reveals = document.querySelectorAll('.reveal, .reveal-wipe');
  var io = new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if(e.isIntersecting){
        e.target.classList.add('is-in');
      }
    });
  }, {threshold:.28});
  reveals.forEach(function(el){ io.observe(el); });

  var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
  var counterEl = document.getElementById('counter');
  var railFill = document.getElementById('railFill');

  function pad(n){ return n < 10 ? '0'+n : ''+n; }

  function onScroll(){
    document.body.classList.add('scrolled');
    var doc = document.documentElement;
    var scrolled = (doc.scrollTop || document.body.scrollTop);
    var height = doc.scrollHeight - doc.clientHeight;
    var pct = height > 0 ? (scrolled / height) * 100 : 0;
    if (railFill) railFill.style.height = pct + '%';

    var mid = window.innerHeight / 2;
    var current = 1;
    slides.forEach(function(s, i){
      var r = s.getBoundingClientRect();
      if(r.top <= mid && r.bottom >= mid){
        current = i + 1;
      }
    });
    if (counterEl) counterEl.textContent = pad(current) + ' / ' + pad(slides.length);
  }

  window.addEventListener('scroll', onScroll, {passive:true});
  onScroll();
})();
`

export const RAIL_AND_COUNTER_HTML = `
  <div class="rail" style="position:fixed;left:0;top:0;width:4px;height:100%;background:rgba(0,0,0,.08);z-index:50;">
    <div class="rail-fill" id="railFill" style="width:100%;height:0%;background:var(--ink);transition:height .08s linear;"></div>
  </div>
  <div class="counter" id="counter" style="position:fixed;right:18px;bottom:18px;z-index:50;font-family:'Big Shoulders',sans-serif;font-weight:600;letter-spacing:.08em;font-size:12px;color:var(--ink);background:rgba(255,255,255,.85);padding:6px 10px;border-radius:2px;">01 / 01</div>
`

/**
 * Modo captura: força `.is-in` em tudo e desliga transições, pra nunca tirar
 * um print no meio de uma animação de entrada (usado pelo render-service e,
 * opcionalmente, por exports estáticos que não devem depender de scroll).
 */
export const CAPTURE_MODE_SCRIPT = `
document.querySelectorAll('.reveal, .reveal-wipe').forEach(function(el){ el.classList.add('is-in'); });
`
export const CAPTURE_MODE_CSS = `
*,
*::before,
*::after{transition:none!important;animation:none!important;}
.reveal{opacity:1!important;}
.reveal-wipe::after{transform:scaleX(0)!important;}
`

interface ShellOptions {
  /** HTML já montado de uma ou mais `<section class="tpl-...">` */
  bodyHtml: string
  /** CSS escopado de cada template usado (deduplicado pelo chamador) */
  templateCss: string
  captureMode?: boolean
  embedMode?: boolean
  slideCount?: number
  title?: string
}

/** Documento completo — usado no "Copiar HTML" e na captura via Playwright. */
export function renderFullDocument({ bodyHtml, templateCss, captureMode = false, embedMode = false, slideCount = 1, title = 'Lorena' }: ShellOptions): string {
  const embedHeight = Math.max(1, slideCount) * 800
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${FONTS_HREF}" rel="stylesheet">
<style>
${BASE_CSS}
${templateCss}
${captureMode ? CAPTURE_MODE_CSS : ''}
${embedMode ? `
html,
body{
  width:100%;
  min-width:0;
  overflow:hidden;
  background:var(--paper);
}
:root{
  --template-vw:6.4px;
  --template-vw-2-15:13.76px;
  --template-vw-2-3:14.72px;
  --template-vw-2-85:18.24px;
  --template-vw-3-05:19.52px;
  --template-vw-3-15:20.16px;
  --template-vw-4-4:28.16px;
  --template-vw-7-6:48.64px;
  --template-vw-11-3:72.32px;
  --template-vw-11-8:75.52px;
  --template-vw-12-7:81.28px;
  --template-vw-17-2:110.08px;
  --template-vw-17-5:112px;
  --template-vw-69:441.6px;
}
.embed-scale-frame{
  position:relative;
  width:100%;
  height:calc(${embedHeight}px * var(--embed-scale, 1));
  overflow:hidden;
  background:var(--paper);
}
.embed-scale-frame .deck{
  position:absolute;
  top:0;
  left:50%;
  width:640px;
  max-width:none;
  margin:0;
  box-shadow:none;
  transform:translateX(-50%) scale(var(--embed-scale, 1));
  transform-origin:top center;
}
` : ''}
</style>
</head>
<body>
${embedMode ? '' : RAIL_AND_COUNTER_HTML}
${embedMode ? '<div class="embed-scale-frame">' : ''}
<div class="deck">
${bodyHtml}
</div>
${embedMode ? '</div>' : ''}
<script>
${embedMode ? `
(function(){
  function resizeEmbed(){
    var scale = Math.min(1.2, window.innerWidth / 640);
    document.documentElement.style.setProperty('--embed-scale', String(scale));
  }
  window.addEventListener('resize', resizeEmbed, {passive:true});
  resizeEmbed();
})();
` : ''}
${REVEAL_SCRIPT}
${captureMode ? CAPTURE_MODE_SCRIPT : ''}
</script>
</body>
</html>`
}

/** Documento leve pro preview de UM slide dentro do iframe do editor. */
export function renderPreviewDocument({ bodyHtml, templateCss }: { bodyHtml: string; templateCss: string }): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${FONTS_HREF}" rel="stylesheet">
<style>
${BASE_CSS}
${templateCss}
body{ background:var(--paper); }
</style>
</head>
<body>
<div class="deck">
${bodyHtml}
</div>
<script>
${REVEAL_SCRIPT}
</script>
</body>
</html>`
}
